import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import honeywellLogo from '../honeywell-logo.png';
import api from '../services/api';
import ShopLayout from '../components/shop/ShopLayout';
import ProductCard from '../components/shop/ProductCard';
import CategoryProductRow from '../components/shop/CategoryProductRow';
import Pagination from '../components/shop/Pagination';
import { mapApiProductToCard } from '../utils/shopHelpers';
import './CategoryPage.css';
import './CategoryPageModern.css';
import Seo, { buildBreadcrumbSchema, buildItemListSchema } from '../components/seo/Seo';
import { categorySeo } from '../config/seo';

const PRICE_RANGES = [
  { label: 'Under KES 50,000', min: '', max: '50000' },
  { label: 'KES 50,000 – 100,000', min: '50000', max: '100000' },
  { label: 'KES 100,000 – 200,000', min: '100000', max: '200000' },
  { label: 'KES 200,000+', min: '200000', max: '' }
];

const CategoryPage = () => {
  const { categoryName } = useParams();
  const [viewMode, setViewMode] = useState('list');
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('featured');
  const [products, setProducts] = useState([]);
  const [brandFilters, setBrandFilters] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  const [stockFilter, setStockFilter] = useState('');

  const categoryTitles = {
    'handheld-computers': 'Mobile Handheld Computer',
    'barcode-printers': 'Barcode Printers',
    'barcode-scanners': 'Barcode Scanners',
    'tablet-computers': 'Tablet Computers',
    'receipt-printers': 'Receipt Printers',
    'barcode-labels': 'Barcode Labels',
    'rfid-readers': 'RFID Readers',
    'autonomous-mobile-robotics': 'Autonomous Mobile Robotics'
  };

  const categoryTitle = categoryTitles[categoryName] || categoryName?.replace(/-/g, ' ') || 'Products';
  const seo = categorySeo(categoryName, categoryTitle);

  const priceRange = useMemo(
    () => PRICE_RANGES.find((r) => r.label === selectedPrice),
    [selectedPrice]
  );

  useEffect(() => {
    setPage(1);
  }, [categoryName, selectedBrand, selectedPrice, stockFilter, sortBy, itemsPerPage]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError('');
        const params = {
          page,
          limit: itemsPerPage,
          sort: sortBy
        };
        if (selectedBrand) params.brand = selectedBrand;
        if (priceRange?.min) params.min_price = priceRange.min;
        if (priceRange?.max) params.max_price = priceRange.max;
        if (stockFilter === 'in-stock') params.in_stock = 'true';
        if (stockFilter === 'out-of-stock') params.in_stock = 'false';

        const res = await api.getPublicCategoryProducts(categoryName, params);
        setProducts(Array.isArray(res?.data) ? res.data : []);
        setBrandFilters(Array.isArray(res?.filters?.brands) ? res.filters.brands : []);
        setPagination(res?.pagination || { total: 0, pages: 1 });
      } catch (e) {
        setProducts([]);
        setError(e.message || 'Could not load products for this category');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [categoryName, page, itemsPerPage, sortBy, selectedBrand, priceRange, stockFilter]);

  const featuredBrands = useMemo(() => {
    const source = brandFilters.length
      ? brandFilters
      : Array.from(new Set(products.map((p) => p.brand_name).filter(Boolean))).map((name) => ({ name }));
    if (source.length === 0) return [{ name: 'Honeywell', logo: honeywellLogo }];
    return source.slice(0, 6).map((b) => ({
      name: b.name,
      logo: b.name?.toLowerCase() === 'honeywell'
        ? honeywellLogo
        : `https://via.placeholder.com/120x36?text=${encodeURIComponent(b.name)}`
    }));
  }, [brandFilters, products]);

  const productCards = products.map(mapApiProductToCard);

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedPrice('');
    setStockFilter('');
  };

  return (
    <ShopLayout breadcrumbs={[{ label: categoryTitle }]}>
      <Seo
        {...seo}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Shop', url: '/shop' },
            { name: categoryTitle, url: seo.path }
          ]),
          products.length ? buildItemListSchema(products, categoryTitle) : null
        ].filter(Boolean)}
      />
      <div className="category-page">
        <section className="category-header-strip">
          <div className="container">
            <div className="breadcrumbs">
              <Link to="/shop">Shop</Link>
              <span>›</span>
              <span>{categoryTitle}</span>
            </div>
            <div className="header-main">
              <div>
                <h1>{categoryTitle}</h1>
                <p>Browse rugged mobile computers, scanners, and enterprise accessories from trusted AIDC brands.</p>
              </div>
              <div className="header-actions">
                <Link to="/contact" className="btn-contact">Talk to an Expert</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="category-main-content">
          <div className="container">
            <div className="content-layout">
              <aside className="filter-sidebar">
                <div className="sidebar-card">
                  <h3>Shop By</h3>
                  {brandFilters.length > 0 && (
                    <details className="filter-group" open>
                      <summary>Brand</summary>
                      <div className="filter-options">
                        {brandFilters.map((b) => (
                          <label key={b.id || b.slug}>
                            <input
                              type="checkbox"
                              checked={selectedBrand === b.slug}
                              onChange={() => setSelectedBrand(selectedBrand === b.slug ? '' : b.slug)}
                            />
                            {b.name}
                          </label>
                        ))}
                      </div>
                    </details>
                  )}
                  <details className="filter-group" open>
                    <summary>Price</summary>
                    <div className="filter-options">
                      {PRICE_RANGES.map((r) => (
                        <label key={r.label}>
                          <input
                            type="radio"
                            name="price"
                            checked={selectedPrice === r.label}
                            onChange={() => setSelectedPrice(selectedPrice === r.label ? '' : r.label)}
                          />
                          {r.label}
                        </label>
                      ))}
                    </div>
                  </details>
                  <details className="filter-group" open>
                    <summary>Availability</summary>
                    <div className="filter-options">
                      <label>
                        <input type="radio" name="stock" checked={stockFilter === ''} onChange={() => setStockFilter('')} />
                        All
                      </label>
                      <label>
                        <input type="radio" name="stock" checked={stockFilter === 'in-stock'} onChange={() => setStockFilter('in-stock')} />
                        In Stock
                      </label>
                      <label>
                        <input type="radio" name="stock" checked={stockFilter === 'out-of-stock'} onChange={() => setStockFilter('out-of-stock')} />
                        Out of Stock
                      </label>
                    </div>
                  </details>
                  {(selectedBrand || selectedPrice || stockFilter) && (
                    <button type="button" className="filter-clear" onClick={clearFilters}>
                      Clear filters
                    </button>
                  )}
                </div>
                <div className="sidebar-card support-card">
                  <h4>Need help choosing?</h4>
                  <p>Our specialists can recommend the best device for your operation.</p>
                  <a href="tel:+254711435629">Call +254 727 450523</a>
                </div>
              </aside>

              <div className="main-content-area">
                <section className="brand-strip">
                  <span>Top brands</span>
                  <div className="brand-strip-logos">
                    {featuredBrands.map((brand) => (
                      <img key={brand.name} src={brand.logo} alt={brand.name} />
                    ))}
                  </div>
                </section>

                <div className="product-controls">
                  <div className="control-left">
                    <label>
                      Items per page:
                      <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                        <option value={12}>12</option>
                        <option value={24}>24</option>
                        <option value={40}>40</option>
                      </select>
                    </label>
                    <span className="results-count">{pagination.total} products</span>
                  </div>
                  <div className="control-center">
                    <label>
                      Sort by:
                      <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="featured">Featured</option>
                        <option value="newest">Newest</option>
                        <option value="best-selling">Best Selling</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                        <option value="name">Name A-Z</option>
                      </select>
                    </label>
                  </div>
                  <div className="control-right">
                    <span>View:</span>
                    <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List</button>
                    <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>Grid</button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center p-4">Loading products...</div>
                ) : error ? (
                  <div className="alert alert-warning mb-0">{error}</div>
                ) : (
                  <div className={`products-list ${viewMode === 'grid' ? 'products-grid-view' : 'products-list-view'}`}>
                    {productCards.map((product) => (
                      viewMode === 'grid' ? (
                        <ProductCard key={product.id} product={product} />
                      ) : (
                        <CategoryProductRow
                          key={product.id}
                          product={product}
                          categoryTitle={categoryTitle}
                        />
                      )
                    ))}
                    {productCards.length === 0 && (
                      <div className="text-center p-4">No products match your filters.</div>
                    )}
                  </div>
                )}

                <Pagination page={page} pages={pagination.pages} onPageChange={setPage} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </ShopLayout>
  );
};

export default CategoryPage;
